import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { useKSK2020_R01Store } from "../Store_KSK2020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultKSK2020_R01InspectDto, KSK2020_R01InspectDto } from "../Dto_KSK2020_R01";
import { Info_KSK2020_R01 } from "../info/Info_KSK2020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useDescText } from "src/hooks/useDescText";
import { DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";

const COLUMN_STATE_KEY = 'KSK2020_R01-inspect-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: [''],
	insertedDisabledCells: [''],
	commonDisabledCells: ['badCase'],
	
	pointerCells: [''],
	insertedPointerCells: ['badCase', 'badQty'],
	commonPointerCells: [''],

	alignLeftCells: [''],
};

const isInspectRowEditable = (data?: KSK2020_R01InspectDto | null) => data?.inserted === true;

interface Props {
	gridRef: React.RefObject<AgGridReact<KSK2020_R01InspectDto> | null>;
	defaultKSK2020_R01InspectRow: KSK2020_R01InspectDto;
}

export const InspectTable_KSK2020_R01 = (props: Props) => {

	const { gridRef, defaultKSK2020_R01InspectRow } = props;

	const { 
			KSK2020_R01InspectList, setKSK2020_R01InspectList, 
			editKSK2020_R01,
			selectedKSK2020_R01InspectList, setSelectedKSK2020_R01InspectList, removeSelectedKSK2020_R01InspectList, addSelectedKSK2020_R01InspectList, initSelectedKSK2020_R01InspectList,
			deletedKSK2020_R01InspectList, setDeletedKSK2020_R01InspectList, addDeletedKSK2020_R01InspectList, removeDeletedKSK2020_R01InspectList,
			insertKSK2020_R01InspectList, updateKSK2020_R01InspectList, removeKSK2020_R01InspectList,
			inspectColumnState, setInspectColumnState,
			originalKSK2020_R01InspectList, setOriginalKSK2020_R01InspectList,
		} = useKSK2020_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { faultyValueList } = useCommonCodeStore();
	const { renderDescText } = useDescText();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: KSK2020_R01InspectList,
		defaultGridData: defaultKSK2020_R01InspectRow,
		selectedRow: selectedKSK2020_R01InspectList,
		originalGridData: originalKSK2020_R01InspectList,
		updateRow: (index: number, data: any) => updateKSK2020_R01InspectList(index, data),
	});

	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		badCaseNm: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"),
		badQty: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"),
	}), [renderDescText]);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2020_R01InspectDto>({
			idKey: 'valueIndex',
			selectedList: selectedKSK2020_R01InspectList,
			deletedList: deletedKSK2020_R01InspectList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedKSK2020_R01InspectList, deletedKSK2020_R01InspectList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	]);

	const commonInputColumns = ['badCase', 'badQty'];
	const defaultEditableColumns = ['badCase', 'badQty'];

	const handleGridDropDownCommitAfter = useMemo(() =>
		createGridDropDownCommitAfter<KSK2020_R01InspectDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<KSK2020_R01InspectDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
        {
			headerName: renderDescText(DESC_FLD.DEFECT_REASON, "불량사유"), colId: 'badCase', field: 'badCase', width: 130,
			...CommonCodeColumn.options(
				faultyValueList,
				(params) => isInspectRowEditable(params.data),
				CommonCodeColumn.change(updateKSK2020_R01InspectList, {
					canChange: ({ data }) => isInspectRowEditable(data),
				}),
				handleGridDropDownCommitAfter,
			),
		},
        {
			headerName: renderDescText(DESC_FLD.DEFECT_QTY, "불량수량"), colId: 'badQty', field: 'badQty', width: 130,
			editable: (params) => isInspectRowEditable(params.data),
			pasteable: true,
		},
		// ...IndexParameterCell(),
	], [deletedKSK2020_R01InspectList, KSK2020_R01InspectList, faultyValueList, updateKSK2020_R01InspectList, handleGridDropDownCommitAfter, renderDescText]);

	const columnDefs: ColDef<KSK2020_R01InspectDto>[] = useMemo(() => {
		if (inspectColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<KSK2020_R01InspectDto>({
			baseColumnDefs,
			columnState: inspectColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, inspectColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: KSK2020_R01InspectList,
		originalDataList: originalKSK2020_R01InspectList,
		updateRow: updateKSK2020_R01InspectList,
		customBeforeUpdate: (updatedData, field) => {
			if (!updatedData.inserted) {
				const original = originalKSK2020_R01InspectList.find(
					item => item.valueIndex === updatedData.valueIndex,
				);
				if (original) {
					return {
						...updatedData,
						[field]: original[field as keyof KSK2020_R01InspectDto],
					};
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2020_R01InspectList,
		idKey: 'valueIndex',
		selectedList: selectedKSK2020_R01InspectList,
		deletedList: deletedKSK2020_R01InspectList,
		addSelected: addSelectedKSK2020_R01InspectList,
		removeSelected: removeSelectedKSK2020_R01InspectList,
		clickable, setClickable
	});

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2020_R01InspectList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<KSK2020_R01InspectDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return isInspectRowEditable(data) && commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(KSK2020_R01InspectList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2020_R01InspectDto>[] = useMemo(() => [
	], [editKSK2020_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(KSK2020_R01InspectList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<KSK2020_R01InspectDto>) => {
		postSortHandler(params, KSK2020_R01InspectList, originalKSK2020_R01InspectList, setKSK2020_R01InspectList);
	}, [KSK2020_R01InspectList, originalKSK2020_R01InspectList, ]);

	const pinnedBottomRowData = useMemo(() => {

		const totalQty : number = KSK2020_R01InspectList
			.reduce((sum, item) => sum + (deletedKSK2020_R01InspectList.includes(item.itemCd) ? 0 : Number(item.badQty) || 0), 0);

		return [{
			badCaseNm: formatDescCount(
				KSK2020_R01InspectList.length,
				renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건"),
			),
			badQty: totalQty,
		}];	  
	}, [KSK2020_R01InspectList, deletedKSK2020_R01InspectList, renderDescText]);

	return (
		<>
			{/* selectedDetailKSK2020_R01 : {JSON.stringify(selectedKSK2020_R01InspectList)}<br /> */}
			
			<GridContextWrapper<KSK2020_R01InspectDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setKSK2020_R01InspectList}
				initSelected={initSelectedKSK2020_R01InspectList}
				defaultRowData={defaultKSK2020_R01InspectRow}
				baseColumnDefs={columnDefs}	
				setColumnState={setInspectColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<KSK2020_R01InspectDto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={gridDefaultColDef}
					overlayNoRowsTemplate={`<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`}

					rowClassRules={createRowClassRules<KSK2020_R01InspectDto>({
						idKey: 'valueIndex',
						selectedList: selectedKSK2020_R01InspectList,
						deletedList: deletedKSK2020_R01InspectList
					})}

					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setInspectColumnState}		//컬럼 상태 저장
				/>
			</GridContextWrapper>
		</>
	)
}
